/** Design-system atoms (design-system.md §6), ported from the canvas markup in design/app.js. */
import {
  limitDecimals,
  rtaState,
  type CategoryMonth,
  type Currency,
  type MemberDto,
  type MoneyState,
  type Target,
} from '@koverta/shared';
import { useEffect, useRef, type ButtonHTMLAttributes, type ComponentProps, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFmt, useHousehold, type Fmt } from '../lib/household';

export const ICONS = {
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  plan: 'M4 6.5 12 12l8-5.5M4 6h16v12H4z',
  inbox: 'M3 13h5l1.5 3h5L16 13h5M5.5 5h13L21 13v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z',
  wallet: 'M19 7V5.5A1.5 1.5 0 0 0 17.5 4H5a2 2 0 0 0 0 4h15v12H5a2 2 0 0 1-2-2V6M16.5 14h.01',
  reflect: 'M4 20V11M10 20V5M16 20v-6M3 20h18',
  forecast: 'M3 17l5-5 4 3 8-8M14 7h6v6',
  settings: 'M4 7h9m4 0h3M4 17h3m4 0h9M15 4.5v5M9 14.5v5',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM20 20l-3.5-3.5',
  check: 'M5 12.5l4.5 4.5L19 7',
  x: 'M6 6l12 12M18 6 6 18',
  chevL: 'M15 6l-6 6 6 6',
  chevR: 'M9 6l6 6-6 6',
  chevD: 'M6 9l6 6 6-6',
  split: 'M6 4v5a5 5 0 0 0 5 5h7M15 10.5 18.5 14 15 17.5M6 20v-3',
  upload: 'M12 15V4M7.5 8.5 12 4l4.5 4.5M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4',
  lock: 'M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  swap: 'M7 4 3 8l4 4M3 8h14M17 20l4-4-4-4M21 16H7',
  phone: 'M8 3h8a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM11 18h2',
  more: 'M6 12h.01M12 12h.01M18 12h.01',
  undo: 'M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
  card: 'M3 6h18v12H3zM3 10h18M7 15h3',
  flag: 'M5 21V4M5 4h12l-2.5 4L17 12H5',
  filter: 'M4 6h16M7 12h10M10 18h4',
  cal: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  memo: 'M5 5h14M5 10h14M5 15h9',
  bolt: 'M13 3 5 13h6l-1 8 8-10h-6z',
  target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 12h.01',
  link: 'M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1',
  merge: 'M7 4v4a5 5 0 0 0 5 5h0a5 5 0 0 1 5 5v2M17 4v4a5 5 0 0 1-5 5',
  kb: 'M3 6h18v12H3zM7 10h.01M11 10h.01M15 10h.01M7 14h10',
  pin: 'M12 17v4M8 3h8l-1 6 3 3v2H6v-2l3-3z',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  logout: 'M15 4h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4M10 16l-4-4 4-4M6 12h10',
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  edit: 'M4 20h4L19 9l-4-4L4 16zM14 6l4 4',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
} as const;
export type IconName = keyof typeof ICONS;

export function Icon({
  name,
  size = 18,
  stroke = 1.7,
  className,
}: {
  name: IconName;
  size?: number;
  stroke?: number;
  className?: string;
}) {
  const w = name === 'more' || name === 'grip' ? 3 : stroke;
  return (
    <svg
      className={`ic ${className ?? ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={w}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONS[name]} />
    </svg>
  );
}

/**
 * The "Ovce na broju" logo, the same art as public/icon.svg. It fills its `.logo` tile, which supplies the
 * size and corner radius; the colours are fixed so the logo looks the same in both themes. The viewBox
 * crops the padding the PWA icon keeps for Android's maskable safe zone, so the sheep reads at 32 px.
 */
export function LogoMark() {
  return (
    <svg viewBox="5.5 9 52 52" width="100%" height="100%" aria-hidden="true">
      <rect width="64" height="64" fill="#1d4e6b" />
      <ellipse cx="35" cy="49.4" rx="16" ry="2.1" fill="#14384d" />
      <path
        d="M28.2 39v9.2M33.6 40v8.2M39.6 40v8.2M45 39v9.2"
        stroke="#f5f3ee"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M36.5 21.2A5.06 5.06 0 0 1 44.73 23.07A4.24 4.24 0 0 1 49.81 27.97A3.63 3.63 0 0 1 49.81 34.03A4.24 4.24 0 0 1 44.73 38.93A5.06 5.06 0 0 1 36.5 40.8A5.06 5.06 0 0 1 28.27 38.93A4.24 4.24 0 0 1 23.19 34.03A3.63 3.63 0 0 1 23.19 27.97A4.24 4.24 0 0 1 28.27 23.07A5.06 5.06 0 0 1 36.5 21.2Z"
        fill="#fff"
      />
      <g transform="rotate(-18 21.5 28.5)">
        <ellipse cx="15.6" cy="23.6" rx="3.9" ry="2" fill="#1b2226" />
        <ellipse cx="21.5" cy="28.5" rx="5.7" ry="7.1" fill="#1b2226" />
        <circle cx="19.6" cy="27.1" r="1.2" fill="#fff" />
      </g>
      <rect
        x="12.6"
        y="24.2"
        width="3.6"
        height="4.4"
        rx="1"
        fill="#f2b33d"
        transform="rotate(-26 14.4 26.4)"
      />
    </svg>
  );
}

const EU_STARS = Array.from({ length: 12 }, (_, k) => {
  const a = (k / 12) * Math.PI * 2;
  return [(6 + Math.cos(a) * 2.5).toFixed(2), (4 + Math.sin(a) * 2.5).toFixed(2)];
});

export function Flag({ currency }: { currency: Currency }) {
  if (currency === 'EUR') {
    return (
      <svg className="flag" viewBox="0 0 12 8" role="img" aria-label="EUR">
        <rect width="12" height="8" fill="#0b3a9a" />
        <g fill="#ffcc00">
          {EU_STARS.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r=".38" />
          ))}
        </g>
      </svg>
    );
  }
  return (
    <svg className="flag" viewBox="0 0 12 8" role="img" aria-label="RSD">
      <rect width="12" height="8" fill="#fff" />
      <rect width="12" height="2.67" fill="#c6363c" />
      <rect y="2.67" width="12" height="2.67" fill="#0c4076" />
    </svg>
  );
}

export function CurrencyChip({ currency, sm }: { currency: Currency; sm?: boolean }) {
  return (
    <span className={`cur ${sm ? 'sm' : ''}`}>
      <Flag currency={currency} />
      {sm ? null : currency}
    </span>
  );
}

export function Avatar({ member, lg, id }: { member?: MemberDto | null; id?: string; lg?: boolean }) {
  const { members } = useHousehold();
  const m = member ?? (id ? members.get(id) : undefined);
  if (!m)
    return (
      <span className={`av ${lg ? 'lg' : ''}`} style={{ ['--c' as string]: '#858c91' }}>
        ?
      </span>
    );
  return (
    <span className={`av ${lg ? 'lg' : ''}`} style={{ ['--c' as string]: m.color }} title={m.displayName}>
      {m.initial}
    </span>
  );
}

export function Avatars({ lg }: { lg?: boolean }) {
  const { household } = useHousehold();
  return (
    <span className="avs">
      {household.members.map((m) => (
        <Avatar key={m.id} member={m} lg={lg} />
      ))}
    </span>
  );
}

export function Pill({
  state,
  value,
  onClick,
  label,
}: {
  state: MoneyState;
  value: string;
  onClick?: () => void;
  label?: string;
}) {
  if (onClick) {
    return (
      <button type="button" className={`pill ${state}`} onClick={onClick} tabIndex={-1} aria-label={label}>
        {value}
      </button>
    );
  }
  return <span className={`pill ${state}`}>{value}</span>;
}

export function CatPill({
  c,
  onClick,
}: {
  c: Pick<CategoryMonth, 'available' | 'state'> & { name?: string };
  onClick?: () => void;
}) {
  const fmt = useFmt();
  return (
    <Pill
      state={c.state}
      value={fmt.num(c.available)}
      onClick={onClick}
      label={c.name ? `${c.name}: ${fmt.num(c.available)} available` : undefined}
    />
  );
}

export const isSnoozedNow = (t: Target | null, month: string) => !!t && t.snoozedMonth === month;

export function TargetBar({
  c,
  month,
  big,
}: {
  c: CategoryMonth & { target: Target | null };
  month: string;
  big?: boolean;
}) {
  if (!c.target) return null;
  const snoozed = isSnoozedNow(c.target, month);
  const { goal, funded } = targetProgress(c);
  const pct = snoozed
    ? 100
    : c.state === 'neg'
      ? 100
      : Math.max(big ? 3 : 4, Math.min(100, goal ? (funded / goal) * 100 : 100));
  return (
    <div className={`bar ${snoozed ? 'snz' : c.state}`} style={big ? { height: 8 } : undefined}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/** An undated savings-balance target has no monthly ask: it is done when the balance reaches the amount. */
export const isBalanceTarget = (t: Target | null | undefined) => t?.type === 'savings_balance' && !t.dueDate;

/** What a target's progress bar measures: this month's need, or (undated savings) the balance against the amount. */
export function targetProgress(c: CategoryMonth & { target: Target | null }) {
  const byBalance = isBalanceTarget(c.target);
  return {
    byBalance,
    goal: byBalance || !c.monthlyNeed ? c.target?.amount || 0 : c.monthlyNeed,
    funded: byBalance ? Math.max(0, c.available) : c.funded,
  };
}

export function statusText(c: CategoryMonth & { target: Target | null }, month: string, fmt: Fmt): string {
  if (c.state === 'neg') return `Overspent ${fmt.num(-c.available)}`;
  if (isSnoozedNow(c.target, month)) return 'Target snoozed this month';
  if (c.state === 'warn') return `${fmt.num(c.need)} more needed this month`;
  if (isBalanceTarget(c.target)) {
    const { goal, funded } = targetProgress(c);
    if (funded < goal) return `${fmt.num(goal - funded)} to reach target`;
  }
  if (c.target)
    return c.activity < 0
      ? c.available > 0
        ? `Funded · ${fmt.num(c.available)} left`
        : 'Funded · fully spent'
      : 'Funded';
  return '';
}

export function targetSentence(t: Target | null, fmt: Fmt): string {
  if (!t) return 'No target';
  const due = t.dueDate ? ` by ${fmt.date(t.dueDate)}${t.dueDate.slice(0, 4)}` : '';
  switch (t.type) {
    case 'spending':
      if (t.mode === 'refill') return `Needed for spending · refill up to ${fmt.num(t.amount)}`;
      return `Needed for spending · ${fmt.num(t.amount)} every ${t.cadence === 'weekly' ? 'week' : t.cadence === 'yearly' ? 'year' : 'month'}`;
    case 'savings_balance':
      return `Savings balance · ${fmt.num(t.amount)}${due}`;
    case 'debt':
      return `Debt payment · ${fmt.num(t.amount)} a month`;
  }
}

export function Confidence({ value }: { value: number }) {
  return (
    <span
      className="conf"
      role="img"
      title={`${Math.round(value * 100)}% confident`}
      aria-label={`${Math.round(value * 100)}% confident`}
    >
      {[0, 0.85, 0.93].map((t, k) => (
        <i key={k} className={value >= t && value > 0 ? 'f' : ''} />
      ))}
    </span>
  );
}

export const Kbd = ({ children, inverse }: { children: ReactNode; inverse?: boolean }) => (
  <span
    className="kbd"
    style={inverse ? { background: 'transparent', color: 'inherit', borderColor: 'currentColor' } : undefined}
  >
    {children}
  </span>
);

/** Native amount with a muted "≈ base" line when the currency differs from the base (design-system §6). */
export function AmountWithBase({
  amount,
  currency,
  base,
  decimals = 2,
  className,
}: {
  amount: number;
  currency: Currency;
  base?: number;
  decimals?: 0 | 2;
  className?: string;
}) {
  const fmt = useFmt();
  return (
    <span className={className}>
      {fmt.money(amount, currency, decimals)}
      {currency !== fmt.base && base !== undefined ? (
        <span className="amt2">
          ≈ {fmt.num(base)} {fmt.base}
        </span>
      ) : null}
    </span>
  );
}

export function RtaLabel({ rta }: { rta: number }) {
  return (
    <>{rta < 0 ? 'Assigned more than you have' : rta === 0 ? 'All money assigned' : 'Ready to Assign'}</>
  );
}
export { rtaState };

export function Seg<T extends string>({
  value,
  options,
  onChange,
  full,
  label,
}: {
  value: T;
  options: [T, ReactNode][];
  onChange: (v: T) => void;
  full?: boolean;
  label?: string;
}) {
  return (
    <div className={`seg2 ${full ? 'full' : ''}`} role="radiogroup" aria-label={label}>
      {options.map(([v, l]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={v === value}
          className={v === value ? 'on' : ''}
          onClick={() => onChange(v)}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

/** Text input for money: decimal keyboard, at most two decimals, "0,00" placeholder. onChange gets the cleaned text. */
export function MoneyInput({
  onChange,
  placeholder,
  ...rest
}: Omit<ComponentProps<'input'>, 'onChange' | 'inputMode'> & {
  onChange: (text: string) => void;
}) {
  const { fmt } = useHousehold();
  return (
    <input
      {...rest}
      inputMode="decimal"
      placeholder={placeholder ?? fmt.num(0, 2)}
      onChange={(e) => onChange(limitDecimals(e.target.value, fmt.locale))}
    />
  );
}

export function Btn({
  variant,
  icon,
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'okb' | 'big' | 'danger';
  icon?: IconName;
}) {
  return (
    <button type="button" className={`btn ${variant ?? ''} ${className ?? ''}`} {...rest}>
      {icon ? <Icon name={icon} size={15} /> : null}
      {children}
    </button>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <div style={{ display: 'grid', justifyItems: 'center', gap: 10 }}>
        <div className="spinner" />
        <span>{label}</span>
      </div>
    </div>
  );
}

/** Bottom sheet (mobile): scrim closes it; primary action sits in the thumb zone. */
export function Sheet({
  onClose,
  children,
  className,
  label,
}: {
  onClose: () => void;
  children: ReactNode;
  className?: string;
  label: string;
}) {
  useEscape(onClose);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className={`sheet ${className ?? ''}`} role="dialog" aria-modal="true" aria-label={label}>
        <div className="grab" />
        {children}
      </div>
    </>
  );
}

/** Desktop dialog, rendered into body so it overlays the whole app. */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  size = 'md',
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEscape(onClose);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button:not(.ib)');
    first?.focus();
  }, []);
  return createPortal(
    <div className="frame" style={{ minHeight: 0, position: 'static' }}>
      <div className="scrim-d" onClick={onClose} />
      <div
        className={`dlg ${size === 'xl' ? '' : size}`}
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <div className="dlg-h">
          <h3>{title}</h3>
          <button className="ib" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="dlg-b">{children}</div>
        {footer ? <div className="dlg-f">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function useEscape(fn: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fn();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [fn]);
}

export function EmptyInbox({ desk, last }: { desk?: boolean; last?: string | null }) {
  return (
    <div className="empty">
      <svg
        width={desk ? 120 : 96}
        height={desk ? 96 : 78}
        viewBox="0 0 120 96"
        fill="none"
        aria-hidden="true"
      >
        <rect
          x="14"
          y="26"
          width="92"
          height="60"
          rx="10"
          fill="var(--pos-bg)"
          stroke="var(--pos-line)"
          strokeWidth="2"
        />
        <path d="M14 34l46 30 46-30" stroke="var(--pos-line)" strokeWidth="2" />
        <circle cx="86" cy="24" r="17" fill="var(--pos)" />
        <path
          d="M78 24l6 6 10-11"
          stroke="var(--surface)"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <h4>All caught up</h4>
      <p>
        Every captured payment has a home.
        {desk ? (
          <>
            <br />
            New Google Pay payments show up here within seconds.
          </>
        ) : null}
      </p>
      {last ? <p style={{ marginTop: 14, fontSize: 12.5 }}>{last}</p> : null}
    </div>
  );
}

/** Merchant logo tile: a stable colour per merchant name (logos are the one place hard-coded colours are allowed). */
const LOGO_COLORS = [
  '#c8232b',
  '#0a5aa0',
  '#0f9bd7',
  '#0050aa',
  '#1b1b1b',
  '#d4001a',
  '#8a5a2b',
  '#2f7a4b',
  '#6a3d9a',
  '#b35900',
];
export function MerchantLogo({ name, size }: { name: string; size?: number }) {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const bg = LOGO_COLORS[h % LOGO_COLORS.length];
  const init = (name.trim()[0] ?? '?').toUpperCase();
  return (
    <span
      className="logo-t"
      style={{ background: bg, ...(size ? { width: size, height: size, fontSize: size * 0.4 } : {}) }}
      aria-hidden="true"
    >
      {init}
    </span>
  );
}

export const FLAG_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'] as const;

export function relTime(iso: string, now = Date.now()): string {
  const s = Math.round((now - Date.parse(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

export const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
